import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import type { BoundingBox } from '../@types/global';
import type {
  PersistUserAnnotationsRequest,
  PersistUserAnnotationsResponse,
} from '../@types/persistUserAnnotations';
import type { CornerstoneFriendlyAnnotation } from '../modules/annotator/types';
import { makeAPIRequestV2 } from '../utils/api';

import { endpoints } from './endpoints';

interface UseCreateUserAnnotationParams extends PersistUserAnnotationsRequest {
  imageId: string;
}

export const useUserAnnotations = () => {
  const appendUserAnnotation = useMutation<
    PersistUserAnnotationsResponse,
    AxiosError,
    UseCreateUserAnnotationParams
  >(async (userAnnotationData: UseCreateUserAnnotationParams) => {
    const { imageId, ...userValidationRequestBody } = userAnnotationData;
    const { data } = await makeAPIRequestV2(
      `/${endpoints.v2}/${endpoints.images}/${imageId}/${endpoints.annotations}/`,
      { data: userValidationRequestBody },
      'POST',
    );
    return data.annotationId;
  });

  const removeUserAnnotation = useMutation<
    void,
    AxiosError,
    { imageManifestId: number; annotationId: string }
  >(async ({ imageManifestId, annotationId }) => {
    await makeAPIRequestV2(
      `/${endpoints.images}/${imageManifestId}/${endpoints.annotations}/${annotationId}`,
      {},
      'DELETE',
    );
    return;
  });

  return { appendUserAnnotation, removeUserAnnotation };
};

export const generateUserAnnotationRequest = (
  newAnnotation: CornerstoneFriendlyAnnotation,
  imageId: string,
): UseCreateUserAnnotationParams => {
  const { handles, type } = newAnnotation;
  const { start, end } = handles;
  const boundingBoxAnnotation: BoundingBox = {
    xMin: start.x,
    yMin: start.y,
    xMax: end.x,
    yMax: end.y,
  };
  return {
    userAnnotation: boundingBoxAnnotation,
    imageId,
    findingType: type!,
  };
};
